import {
  AuctionType,
  DateValue,
  EAuctionBiddingEngine,
  EAuctionBiddingStatus,
  EAuctionStatus,
  EAuctionType,
  EBrand,
  ELotStatus,
} from '@bonhams1793/bonhams-typescript'
import getConfig from 'next/config'
import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useStore } from '@nx/app-store'
import {
  ActionButtons,
  ChannelState,
  FinishedLotStatus,
  UserLot,
  buildLotChannelName,
} from '@nx/bidding-engine'
import { usePusher } from '@nx/external/pusher'
import { FireLink } from '@nx/fire/link'
import { Body } from '@nx/fire/typography'
import { canAuctionBeRegisteredFor, canLotBeBidOn } from '@nx/helpers'
import { getTranslate } from '@nx/translations'

import { BiddingWidget } from '@web/components/bidding/BiddingWidget/BiddingWidget'
import { useModals } from '@web/components/common/ModalManager/ModalContext'
import { useGetUser, useGetUserAddresses } from '@web/helpers/api'
import { useUserRegistrations } from '@web/helpers/api/user/useUserRegistrations'

import { StyledBiddingWidgetWrapper } from './LotPageBiddingWidget.styles'
import translate from './LotPageBiddingWidget.translate.json'

const { publicRuntimeConfig } = getConfig()
const { text } = getTranslate(translate)

export function LotPageBiddingWidget({
  auction,
  lot,
  isAddressInRegistrationModalEnabled = false,
}: LotPageBiddingWidgetProps) {
  const [refreshCounter, setRefreshCounter] = useState(0)

  const { data: user } = useGetUser()
  const { data: userAddresses } = useGetUserAddresses({
    clientNo: user?.customerId,
  })
  const { userRegistrations } = useUserRegistrations()
  const { dispatch } = useModals()
  const { pusherConnectionStatus } = usePusher()
  const userLotData: UserLot | undefined = useStore(
    useShallow((store) => store.user.lots[lot.lotUniqueId])
  )

  const [lotFinishedData, setLotFinishedData] = useState<{
    hammerPrice: number | null
    hammerPremium: number | null
    status: ELotStatus
  } | null>(null)
  const channelName = buildLotChannelName(lot.lotUniqueId)
  const channel: ChannelState | undefined = useStore(
    useShallow((store) => store.channels[channelName])
  )

  const auctionCanBeBidOn = canAuctionBeRegisteredFor({
    auctionType: auction.auctionType,
    auctionStatus: auction.auctionStatus,
    biddingStatus: auction.biddingStatus,
    biddingEngine: lot.biddingEngine,
  })

  const lotCanBeBidOn = canLotBeBidOn({
    auctionType: auction.auctionType,
    biddableFrom: auction.startDate,
    biddingEngine: lot.biddingEngine,
    isExhibition: lot.isExhibition,
    isPreview: lot.isPreviewLot,
    isToBeSold: lotFinishedData ? false : lot.isToBeSold,
  })

  const userApprovedToBid = !!userRegistrations.find(
    (registration) => String(registration.iSaleNo) === String(auction.auctionId)
  )?.approvedToBid

  function showLoginModal() {
    dispatch?.({
      type: 'OPEN',
      payload: {
        modal: 'LOGIN',
        props: {
          brand: lot.brand,
          user: null,
        },
      },
    })
  }

  function showRegisterModal() {
    dispatch?.({
      type: 'OPEN',
      payload: {
        modal: 'REGISTER_NOW',
        props: {
          auctionId: auction.auctionId,
          lotUniqueId: lot.lotUniqueId,
          lotId: lot.lotNo,
          isPreview: lot.isPreviewLot,
          trackingLocation: '',
          /* istanbul ignore next */
          showBidModal: /* istanbul ignore next */ () => {
            dispatch?.({ type: 'CLOSE', payload: undefined })
          },
        },
      },
    })
  }

  return auctionCanBeBidOn && lotCanBeBidOn ? (
    <StyledBiddingWidgetWrapper>
      <BiddingWidget
        key={`${lot.lotUniqueId}-${refreshCounter}`}
        auction={{
          auctionId: auction.auctionId,
          startDate: auction.startDate,
          biddingStatus: auction.biddingStatus,
          auctionStatus: auction.auctionStatus,
          auctionType: auction.auctionType,
        }}
        biddingStyle="dropdown"
        lot={{
          currencySymbol: lot.currencySymbol,
          hammerTime: lot.hammerTime,
          isExhibition: lot.isExhibition,
          isPreviewLot: lot.isPreviewLot,
          isToBeSold: lot.isToBeSold,
          lotUniqueId: lot.lotUniqueId,
        }}
        timerStyle="flipClock"
        onLotFinished={setLotFinishedData}
        refreshCallback={() => setRefreshCounter((state) => state + 1)}
      />

      <ActionButtons
        isLoggedIn={!!user}
        showLoginModal={showLoginModal}
        showRegisterModal={showRegisterModal}
        userHasAnAddress={
          typeof userAddresses !== 'undefined'
            ? !!userAddresses.length
            : undefined
        }
        userRegistrations={userRegistrations}
        userHasBids={userLotData?.hasActiveBid}
        lot={{
          auctionId: String(auction.auctionId),
          auctionStatus: auction.auctionStatus,
          auctionType: auction.auctionType,
          biddableFrom: auction.startDate,
          biddingStatus: auction.biddingStatus,
          isExhibition: lot.isExhibition,
          isPreview: lot.isPreviewLot,
          isToBeSold: lot.isToBeSold,
          biddingEngine: lot.biddingEngine,
        }}
        variant="primary"
        isAddressInRegistrationModalEnabled={
          isAddressInRegistrationModalEnabled
        }
        hideBiddingButtons={Boolean(
          channel?.subscriptionStatus !== 'SUBSCRIBED' ||
            pusherConnectionStatus === 'DISCONNECTED'
        )}
      />

      {pusherConnectionStatus !== 'DISCONNECTED' && userApprovedToBid ? (
        <Body $level="XS">
          {text('biddingDisclaimer')}{' '}
          <FireLink
            href={`${publicRuntimeConfig.WEB_BASE_URL}${
              auction.auctionType === AuctionType.online
                ? '/how-to-buy/how-to-bid-online/'
                : '/how-to-buy/how-to-bid-live/'
            }`}
          >
            {text('BiddingDisclaimerLink')}
          </FireLink>
        </Body>
      ) : null}
    </StyledBiddingWidgetWrapper>
  ) : !lot.isToBeSold || !!lotFinishedData ? (
    <StyledBiddingWidgetWrapper>
      <FinishedLotStatus
        lotStatus={lotFinishedData?.status ?? lot.status}
        hammerPrice={lotFinishedData?.hammerPrice ?? lot.hammerPrice}
        hammerPremium={lotFinishedData?.hammerPremium ?? lot.hammerPremium}
        currencySymbol={lot.currencySymbol}
        isUserWinner={!!userLotData?.won}
        invoiceId={
          userLotData?.invoiceId ? String(userLotData.invoiceId) : undefined
        }
        isResultPublished={lot.isResultPublished || !!lotFinishedData}
      />
    </StyledBiddingWidgetWrapper>
  ) : null
}

export interface LotPageBiddingWidgetProps {
  auction: {
    auctionId: string
    startDate: DateValue
    biddingStatus: EAuctionBiddingStatus
    auctionStatus: EAuctionStatus
    auctionType: EAuctionType
  }
  lot: {
    biddingEngine: EAuctionBiddingEngine
    brand: EBrand
    currencySymbol: string
    hammerPremium: number
    hammerPrice: number
    hammerTime: DateValue
    isExhibition: boolean
    isPreviewLot: boolean
    isResultPublished: boolean
    isToBeSold: boolean
    lotNo: string
    lotUniqueId: string
    status: ELotStatus
  }
  isAddressInRegistrationModalEnabled?: boolean
}
